import { Button, useModal } from '@farmshare/ui-components';
import { RecursivePartial } from '@farmshare/utils';
import { faEdit, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FormikProps } from 'formik';
import { concat, filter, map, omit, set } from 'lodash';
import { useRecoilValue } from 'recoil';
import { userState } from 'state';

import {
  Cutsheet,
  CutsheetCreateOneMutationVariables,
  EnumCutsheetAnimalSpecies,
  EnumCutsheetType,
  EnumProcessorSchedulingAnimalSpecies,
  ProcessorCapability,
  ProcessorSettings,
  useCutsheetCreateOneMutation,
  useCutsheetSoftDeleteMutation,
  ViewSchedulingCreateQuery,
} from 'lib/graphql';

import {
  EditCutsheetFormData,
  Extras,
  Trim,
} from 'pages/processor/cutsheets/_views/form-sections';

import { SchedulingCustomCutsheetModal } from './custom-cutsheet-modal';

interface CustomizeCutsheetButtonProps {
  fP: FormikProps<EditCutsheetFormData>;
  cutsheet?: Cutsheet;
  cutsheetId: string | undefined;
  userCutsheets: ViewSchedulingCreateQuery['cutsheetMany'];
  setUserCutsheets: (
    newUserCutsheets: ViewSchedulingCreateQuery['cutsheetMany'],
  ) => void;
  capabilities?: RecursivePartial<ProcessorCapability>[];
  animalSpecies?: EnumProcessorSchedulingAnimalSpecies;
  settings?: RecursivePartial<ProcessorSettings>;
}

export function CustomizeCutsheetButton({
  fP,
  capabilities,
  animalSpecies,
  settings,
  cutsheet,
  cutsheetId,
  setUserCutsheets,
  userCutsheets,
}: CustomizeCutsheetButtonProps) {
  const user = useRecoilValue(userState);

  const { save } = useModal();

  const [createCutsheet, createCutsheetOp] = useCutsheetCreateOneMutation();
  const [deleteCutsheet, deleteCutsheetOp] = useCutsheetSoftDeleteMutation();

  return (
    <Button
      content="Customize Cutsheet"
      icon={faEdit}
      onClick={() => {
        fP.setFieldValue('name', `${fP.values.name} - Copy`);
        save<EditCutsheetFormData>({
          type: 'save',
          title: 'Custom Cutsheet',
          // the initial values don't need to be passed in here, they are already set in the formik form but are required for the save modal
          initialValues: {
            name: `${fP.values.name} - Copy`,
            cuts: fP.values.cuts,
            animalSpecies:
              animalSpecies as unknown as EnumCutsheetAnimalSpecies,
            extras: fP.values.extras,
            trim: fP.values.trim,
            blockedBy: fP.values.blockedBy,
            canSelectMoreThanOneTrim: true,
            selectedSpecifications: {},
            trimSelection: fP.values.trimSelection,
            replaceExistingCutsheet: 'replace',
            cutsheetType: 'user',
            type: 'user',
            userId: user?._id,
          },
          isLoading: createCutsheetOp.loading || deleteCutsheetOp.loading,
          icon: faPlus,
          fullscreen: true,
          body: (fP) => (
            <SchedulingCustomCutsheetModal
              fP={fP}
              capabilities={capabilities}
              animalSpecies={animalSpecies}
              settings={settings}
              cutsheet={cutsheet}
              cutsheetId={cutsheetId}
              currentUserId={user?._id}
            />
          ),
          onSubmit: async ({
            name,
            cuts,
            selectedSpecifications,
            activeSpecification,
            extras,
            trim,
            notes,
            inspectionLevel,
            replaceExistingCutsheet,
            type,
            userId,
          }) => {
            const formattedExtras = animalSpecies
              ? (map(
                  filter(extras, (extra) => extra.isActive) || [],
                  (extra) => ({
                    ...omit(extra, ['__typename', 'disabled', 'description']),
                    quantity: Number(extra.quantity),
                  }),
                ) as Extras[])
              : [];
            const getFormattedTrim = () => {
              if (!animalSpecies) {
                return [];
              }

              return map(
                filter(trim, (trim) => trim.isActive) || [],
                (trim) => ({
                  ...omit(trim, ['__typename', 'disabled', 'description']),
                  quantity: Number(trim.quantity),
                }),
              ) as Trim[];
            };
            const variables: CutsheetCreateOneMutationVariables = {
              record: {
                parentCutsheet: cutsheet?._id,
                type:
                  type === 'vendor'
                    ? EnumCutsheetType.Vendor
                    : EnumCutsheetType.User,
                animalSpecies:
                  animalSpecies as unknown as EnumCutsheetAnimalSpecies,
                inspectionLevels: [inspectionLevel!],
                primalCuts: map(cuts, (c) => c.value),
                user: userId,
                name,
                selectedSpecifications,
                activeSpecification,
                extras: formattedExtras,
                trim: getFormattedTrim(),
                notes,
                vendor: settings?.vendor?._id ?? (settings?.vendor as string),
                createdBy: user?._id,
                updatedBy: user?._id,
              },
            };
            const response = await createCutsheet({
              variables,
            });

            if (!response.data?.cutsheetCreateOne?.record) {
              return;
            }

            // If replacing and not copying, delete the original cutsheet
            if (replaceExistingCutsheet !== 'copy' && cutsheetId) {
              await deleteCutsheet({
                variables: { cutsheetId },
              });
            }

            // Update the local cutsheet list
            setUserCutsheets(
              concat(
                filter(
                  userCutsheets,
                  (c) =>
                    c._id !== cutsheetId || replaceExistingCutsheet === 'copy',
                ),
                [response.data.cutsheetCreateOne.record as Cutsheet],
              ),
            );
          },

          validate: async (values) => {
            const errors: Record<string, any> = {};

            if (!animalSpecies) {
              return errors;
            }

            if (
              !values.inspectionLevel ||
              (values.inspectionLevel as string) === 'Select one...'
            ) {
              set(
                errors,
                'inspectionLevel',
                'Please select one inspection level.',
              );
            }

            // validate quantity for active extras
            const extrasOptions = values.extras ?? [];

            if (extrasOptions.length > 0) {
              for (let i = 0; i < extrasOptions.length; i++) {
                if (
                  extrasOptions[i].isActive &&
                  (extrasOptions[i].quantity ?? 0) <
                    (extrasOptions[i].minLbs ?? 0)
                ) {
                  const extrasErrors = errors.extras || [];
                  extrasErrors[i] = {
                    ...(extrasErrors[i] ?? {}),
                    quantity: `Min quantity is ${extrasOptions[i].minLbs} lbs`,
                  };

                  errors.extras = extrasErrors;
                }
              }
            }

            // validate trims
            const trimOptions = animalSpecies ? (values.trim ?? []) : [];

            if (trimOptions.length > 0) {
              for (let i = 0; i < trimOptions.length; i++) {
                // validate quantity for active trims
                if (
                  trimOptions[i].isActive &&
                  values.trimSelection === 'allowSelectionByWeight' &&
                  (trimOptions[i].quantity ?? 0) < (trimOptions[i].minLbs ?? 0)
                ) {
                  const trimErrors = errors.trim || [];
                  trimErrors[i] = {
                    ...(trimErrors[i] ?? {}),
                    quantity: `Min quantity is ${trimOptions[i].minLbs} lbs`,
                  };

                  errors.trim = trimErrors;
                }
                if (
                  trimOptions[i].isActive &&
                  values.trimSelection === 'allowSelectionByPercentage' &&
                  (trimOptions[i].quantity ?? 0) < 0
                ) {
                  const trimErrors = errors.trim || [];
                  trimErrors[i] = {
                    ...(trimErrors[i] ?? {}),
                    quantity: `Percentage must be positive`,
                  };

                  errors.trim = trimErrors;
                }

                // check if rank is unique for active trims
                const ranks = map(
                  filter(trimOptions, (t) => t?.isActive),
                  (t) => t.rank,
                );
                if (ranks.length !== new Set(ranks).size) {
                  const trimErrors = errors.trim || [];

                  if (trimOptions[i]?.isActive) {
                    trimErrors[i] = {
                      ...(trimErrors[i] ?? {}),
                      rank: 'Rank must be unique',
                    };
                  }

                  errors.trim = trimErrors;
                }
              }
            }

            // check if rank is greater than 0 for active trims
            if (trimOptions.length > 0 && animalSpecies) {
              for (let i = 0; i < trimOptions.length; i++) {
                if (trimOptions[i]?.isActive && trimOptions[i]?.rank < 1) {
                  const trimErrors = errors.trim || [];

                  trimErrors[i] = {
                    ...(trimErrors[i] ?? {}),
                    rank: 'Rank must be greater than 0',
                  };
                  errors.trim = [...trimErrors];
                }
              }
            }

            // check if total percentage is 100 for active trims
            if (
              values.trimSelection === 'allowSelectionByPercentage' &&
              trimOptions.length > 0
            ) {
              const totalPercentage = trimOptions.reduce((acc, trim) => {
                if (trim.isActive) {
                  return acc + (trim.quantity ?? 0);
                }
                return acc;
              }, 0);
              const activeTrims = filter(
                trimOptions,
                (t) => t?.isActive,
              ).length;

              if (activeTrims > 0 && totalPercentage !== 100) {
                errors.trimSelection =
                  'Total percentage must be 100% for all active trims';
              }
            }

            // check if more than one trim can be selected
            if (!values.canSelectMoreThanOneTrim && trimOptions.length > 0) {
              const activeTrims = filter(trimOptions, (t) => t?.isActive);

              if (activeTrims.length > 1) {
                errors.canSelectMoreThanOneTrim =
                  'Only one trim can be selected';
              }
            }

            return errors;
          },
        });
      }}
    />
  );
}
